/**
 * The inventory Table mode's columns (Phase C2): which exist, which are shown (saved per device),
 * their grid tracks, and how a header click maps onto the list's EXISTING server sorts — a column
 * the server cannot sort by is simply not sortable here.
 */

import type { InventorySortOption } from './inventoryListQuery'

export type InventoryTableColumnId =
  | 'image'
  | 'name'
  | 'setNumber'
  | 'game'
  | 'condition'
  | 'printing'
  | 'quantity'
  | 'listed'
  | 'manual'
  | 'sale'
  | 'market'
  | 'cost'
  | 'margin'
  | 'tags'
  | 'channels'
  | 'sync'
  | 'updated'
  | 'lastSold'
  | 'unitsSold'
  | 'lastSalePrice'

export interface InventoryTableColumn {
  id: InventoryTableColumnId
  label: string
  /** A CSS grid track. */
  width: string
  /** Numbers line up on the right. */
  numeric?: boolean
  /** The server sorts a header click maps to (first click = `first`). */
  sort?: { asc: InventorySortOption; desc: InventorySortOption; first: 'asc' | 'desc' }
  /** Always shown (the row needs a name). */
  locked?: boolean
  /** Shown before the user picks their own columns. */
  defaultVisible?: boolean
}

export const INVENTORY_TABLE_COLUMNS: readonly InventoryTableColumn[] = [
  { id: 'image', label: 'Image', width: '44px', defaultVisible: true },
  { id: 'name', label: 'Name', width: 'minmax(180px, 2.4fr)', locked: true, defaultVisible: true, sort: { asc: 'name_asc', desc: 'name_desc', first: 'asc' } },
  { id: 'setNumber', label: 'Set / No.', width: 'minmax(110px, 1.2fr)', defaultVisible: true, sort: { asc: 'set_number_asc', desc: 'set_number_desc', first: 'asc' } },
  { id: 'game', label: 'Game', width: 'minmax(90px, 0.9fr)' },
  { id: 'condition', label: 'Condition', width: 'minmax(90px, 0.9fr)', defaultVisible: true },
  { id: 'printing', label: 'Printing', width: 'minmax(80px, 0.8fr)', defaultVisible: true },
  { id: 'quantity', label: 'Qty', width: '84px', numeric: true, defaultVisible: true },
  { id: 'listed', label: 'Listed', width: '112px', numeric: true, defaultVisible: true, sort: { asc: 'price_asc', desc: 'price_desc', first: 'desc' } },
  { id: 'manual', label: 'Manual', width: '96px', numeric: true },
  { id: 'sale', label: 'Sale', width: '96px', defaultVisible: true },
  { id: 'market', label: 'Market', width: '96px', numeric: true, defaultVisible: true },
  { id: 'cost', label: 'Cost', width: '96px', numeric: true },
  { id: 'margin', label: 'Margin', width: '104px', numeric: true },
  { id: 'tags', label: 'Tags', width: 'minmax(100px, 1fr)' },
  { id: 'channels', label: 'Channels', width: '112px' },
  { id: 'sync', label: 'Sync', width: '84px', defaultVisible: true },
  { id: 'updated', label: 'Updated', width: '104px', sort: { asc: 'updated_asc', desc: 'updated_desc', first: 'desc' } },
  { id: 'lastSold', label: 'Last sold', width: '96px', sort: { asc: 'last_sold_asc', desc: 'last_sold_desc', first: 'desc' } },
  { id: 'unitsSold', label: 'Sold', width: '84px', numeric: true, sort: { asc: 'units_sold_asc', desc: 'units_sold_desc', first: 'desc' } },
  { id: 'lastSalePrice', label: 'Last price', width: '104px', numeric: true },
]

const BY_ID = new Map(INVENTORY_TABLE_COLUMNS.map((column) => [column.id, column]))

export const DEFAULT_TABLE_COLUMNS: InventoryTableColumnId[] = INVENTORY_TABLE_COLUMNS
  .filter((column) => column.defaultVisible)
  .map((column) => column.id)

export const TABLE_COLUMNS_STORAGE_KEY = 'tcgeasy:inventory-table-columns'

/** Known ids only, in the table's own order, the locked ones always in. */
export function normaliseTableColumns(ids: readonly unknown[]): InventoryTableColumnId[] {
  const wanted = new Set(ids.filter((id): id is InventoryTableColumnId => typeof id === 'string' && BY_ID.has(id as InventoryTableColumnId)))
  return INVENTORY_TABLE_COLUMNS.filter((column) => column.locked || wanted.has(column.id)).map((column) => column.id)
}

/** The columns this device chose, or the defaults. Never throws (private mode, blocked storage). */
export function readTableColumns(storage: Pick<Storage, 'getItem'> | null = safeStorage()): InventoryTableColumnId[] {
  try {
    const raw = storage?.getItem(TABLE_COLUMNS_STORAGE_KEY)
    if (!raw) return DEFAULT_TABLE_COLUMNS
    const parsed: unknown = JSON.parse(raw)
    return Array.isArray(parsed) ? normaliseTableColumns(parsed) : DEFAULT_TABLE_COLUMNS
  } catch {
    return DEFAULT_TABLE_COLUMNS
  }
}

export function writeTableColumns(ids: readonly InventoryTableColumnId[], storage: Pick<Storage, 'setItem'> | null = safeStorage()): void {
  try {
    storage?.setItem(TABLE_COLUMNS_STORAGE_KEY, JSON.stringify(ids))
  } catch {
    // A preference: the table works without it.
  }
}

function safeStorage(): Storage | null {
  try {
    return typeof window === 'undefined' ? null : window.localStorage
  } catch {
    return null
  }
}

/** Show or hide one column (a locked column stays). */
export function toggleTableColumn(visible: readonly InventoryTableColumnId[], id: InventoryTableColumnId): InventoryTableColumnId[] {
  if (BY_ID.get(id)?.locked) return [...visible]
  return normaliseTableColumns(visible.includes(id) ? visible.filter((v) => v !== id) : [...visible, id])
}

export function columnsFor(ids: readonly InventoryTableColumnId[]): InventoryTableColumn[] {
  return ids.map((id) => BY_ID.get(id)).filter((column): column is InventoryTableColumn => Boolean(column))
}

/** What the Sold out and Recent sales views always show, in place of what says least there. */
export const SALES_VIEW_COLUMNS: readonly InventoryTableColumnId[] = ['lastSold', 'unitsSold', 'lastSalePrice']
export const SALES_VIEW_HIDDEN_COLUMNS: readonly InventoryTableColumnId[] = ['sale', 'sync']

/**
 * The device's chosen columns as a sales view shows them, the sale figures straight after what
 * names the row so a narrow screen scrolls something else off. The choice itself is left alone.
 */
export function salesViewColumnIds(visible: readonly InventoryTableColumnId[]): InventoryTableColumnId[] {
  const kept = normaliseTableColumns(visible.filter((id) => !SALES_VIEW_HIDDEN_COLUMNS.includes(id) && !SALES_VIEW_COLUMNS.includes(id)))
  const at = Math.max(kept.indexOf('name'), kept.indexOf('setNumber')) + 1
  return [...kept.slice(0, at), ...SALES_VIEW_COLUMNS, ...kept.slice(at)]
}

/** The grid template: the tick box, then each visible column. */
export function tableGridTemplate(columns: readonly InventoryTableColumn[]): string {
  return ['36px', ...columns.map((column) => column.width)].join(' ')
}

/** Column gap and row side padding in InventoryTable.module.css. */
const COLUMN_GAP_PX = 8
const ROW_PADDING_PX = 20

/**
 * The narrowest a row may get before the table scrolls sideways: every track's minimum, plus gaps
 * and padding. Wider screens share the rest out by the `fr` tracks.
 */
export function tableMinWidth(columns: readonly InventoryTableColumn[]): number {
  const minOf = (width: string) => Number(/^(?:minmax\()?(\d+)px/.exec(width)?.[1] ?? 0)
  const tracks = [36, ...columns.map((column) => minOf(column.width))]
  return tracks.reduce((sum, px) => sum + px, 0) + COLUMN_GAP_PX * (tracks.length - 1) + ROW_PADDING_PX
}

/** The sort a header click asks for: the column's first direction, then flip. */
export function nextSortFor(column: InventoryTableColumn, current: InventorySortOption): InventorySortOption | null {
  if (!column.sort) return null
  if (current === column.sort.asc) return column.sort.desc
  if (current === column.sort.desc) return column.sort.asc
  return column.sort[column.sort.first]
}

export function ariaSortFor(column: InventoryTableColumn, current: InventorySortOption): 'ascending' | 'descending' | 'none' | undefined {
  if (!column.sort) return undefined
  if (current === column.sort.asc) return 'ascending'
  if (current === column.sort.desc) return 'descending'
  return 'none'
}
